import { createContext, useContext } from 'react';

// Textos del portal del cliente, en español e inglés (Jamaica y otros clientes en inglés).

export type Lang = 'es' | 'en';

const es = {
  portal: 'Portal de soporte',
  poweredBy: 'Con tecnología de Veta Desk',
  signIn: 'Entrar',
  signInTitle: 'Entra al portal de soporte',
  signInBody: 'Escribe tu correo de trabajo. Te enviaremos un código de 6 dígitos; no necesitas contraseña.',
  email: 'Correo',
  sendCode: 'Enviarme un código',
  sending: 'Enviando…',
  codeSent: (email: string) => `Si ${email} tiene acceso, te llegó un código. Revisa también la carpeta de spam.`,
  code: 'Código',
  verify: 'Entrar',
  otherEmail: 'Usar otro correo',
  resend: 'Reenviar código',
  signOut: 'Salir',
  language: 'English',
  myTickets: 'Mis tickets',
  companyTickets: 'Toda mi empresa',
  newTicket: 'Nuevo ticket',
  awaitingYou: 'Esperan tu aprobación',
  empty: 'No hay tickets todavía.',
  emptyBody: 'Cuando levantes un ticket, aquí verás su avance.',
  updated: 'Actualizado',
  requester: 'Solicitante',
  attendedBy: 'Lo atiende',
  noOneYet: 'Por asignar',
  back: '← Mis tickets',
  loading: 'Cargando…',
  genericError: 'Algo salió mal. Inténtalo de nuevo.',
  kinds: {
    incident: ['Reportar una falla', 'Algo dejó de funcionar o da un resultado incorrecto.'],
    service: ['Pedir un servicio', 'Un alta, un acceso, una consulta o una tarea de rutina.'],
    change: ['Pedir un cambio o desarrollo', 'Algo nuevo o distinto: reporte, configuración, regla. Lo estimamos y te cotizamos antes de iniciar.']
  },
  whatDoYouNeed: '¿Qué necesitas?',
  subject: 'Asunto',
  subjectHint: 'Una línea que lo resuma',
  description: 'Descripción',
  descriptionHint: 'Qué pasa, desde cuándo, a quién afecta y cómo reproducirlo. Puedes adjuntar archivos después de crearlo.',
  impact: '¿A quiénes afecta?',
  impactOpts: { low: 'Solo a mí', medium: 'A un área o grupo', high: 'A toda la empresa' },
  urgency: '¿Qué tan urgente es?',
  urgencyOpts: { low: 'Puede esperar', medium: 'Lo necesito pronto', high: 'Detiene la operación ahora' },
  create: 'Levantar ticket',
  creating: 'Enviando…',
  conversation: 'Conversación',
  you: 'Tú',
  support: 'Soporte',
  reply: 'Responder',
  replyPlaceholder: 'Escribe tu mensaje…',
  send: 'Enviar',
  attach: 'Adjuntar archivo',
  uploading: 'Subiendo…',
  closedNote: 'Este ticket está cerrado. Si necesitas algo más, levanta uno nuevo.',
  resolvedTitle: 'Lo dimos por resuelto',
  resolvedBody: '¿Quedó todo bien?',
  confirm: 'Sí, cerrar el ticket',
  reopen: 'No, sigue fallando',
  reopenPlaceholder: 'Cuéntanos qué sigue sin funcionar',
  quoteTitle: 'Cotización',
  quoteWork: 'Trabajo',
  quotePhase: 'Rubro',
  quoteFunctional: 'Funcional',
  quoteSubtotal: 'Subtotal',
  quoteMgmt: (p: number) => `Gestión del servicio (${p} %)`,
  quoteTotal: 'Total',
  quoteHours: (h: number) => `${h} horas`,
  quoteBag: (h: number) => `Consume horas de tu contrato: se descuentan ${h} h.`,
  quoteAdditional: 'No consume horas de tu contrato: se factura por separado.',
  validUntil: (d: string) => `Vigente hasta el ${d}`,
  expired: 'La cotización venció. Pediremos que la actualicen.',
  approve: 'Aprobar',
  requestChanges: 'Pedir ajustes',
  reject: 'Rechazar',
  reasonChanges: '¿Qué ajustes necesitas?',
  reasonReject: 'Motivo del rechazo',
  onlyApprover: 'La aprobación la hace un aprobador de tu empresa.',
  decision: {
    approved: (by: string) => `Aprobada por ${by}`,
    rejected: (by: string) => `Rechazada por ${by}`,
    changes: (by: string) => `${by} pidió ajustes`
  },
  statements: 'Estados de cuenta',
  statement: 'Estado de cuenta',
  statementsIntro: 'Cada mes te enviamos el estado de cuenta de tu contrato. Revísalo y acéptalo, o pide una aclaración si algo no cuadra.',
  noStatements: 'Todavía no hay estados de cuenta.',
  statementStatus: { sent: 'Por revisar', in_clarification: 'En aclaración', closed: 'Cerrado' } as Record<string, string>,
  stmtCharged: 'Horas cargadas al contrato',
  stmtAdjustments: 'Ajustes',
  stmtBagUsed: (used: number, max: number) => `Bolsa: ${used} de ${max} h usadas`,
  stmtBagLeft: (h: number) => `Quedan ${h} h`,
  stmtBaseline: (used: number, avail: number) => `${used} h de ${avail} h disponibles en el mes`,
  stmtInBand: (h: number) => `${h} h en banda (se descuentan después)`,
  stmtOutOfBand: (h: number) => `${h} h fuera de banda (se facturan aparte)`,
  stmtRollover: (h: number) => `Pasan ${h} h al mes siguiente`,
  stmtTicket: 'Ticket',
  stmtHours: 'Horas',
  stmtNoTicket: 'Actividades del contrato sin ticket',
  stmtDue: (d: string) => `Si no recibimos aclaraciones, se dará por aceptado el ${d}.`,
  stmtAccept: 'Aceptar',
  stmtClarify: 'Pedir aclaración',
  stmtClarifyLine: 'Aclarar',
  stmtClarifyWhat: '¿Qué hay que aclarar?',
  stmtAccepted: (by: string) => `Aceptado por ${by}`,
  stmtAutoAccepted: 'Aceptado por vencimiento del plazo',
  stmtClosedByTeam: 'Cerrado por el equipo de soporte',
  stmtPending: 'Pendiente de respuesta',
  stmtAnswer: 'Respuesta',
  stmtPrint: 'Imprimir / PDF',
  adjustmentKind: { courtesy: 'Cortesía', warranty: 'Garantía', reclass: 'Reclasificación', extra_charge: 'Cargo extra', prior_period: 'Periodo anterior' } as Record<string, string>,
  cancel: 'Cancelar',
  confirmAction: 'Confirmar',
  delivery: {
    development: 'En desarrollo', uat: 'Te toca probar', correction: 'En corrección', deployment: 'Pasando a producción', closed: 'Entregado'
  } as Record<string, string>,
  uatTitle: (round: number) => `Pruebas del usuario${round > 1 ? ` · ronda ${round}` : ''}`,
  uatIntro: 'Prueba el cambio y marca cada criterio de aceptación. Si algo no funciona, recházalo y cuéntanos qué falla.',
  uatAutoAccept: (d: string) => `Si no recibimos respuesta, se darán por aceptadas el ${d}.`,
  uatAccept: 'Aceptado',
  uatReject: 'No funciona',
  uatWhat: '¿Qué falla?',
  uatSubmit: 'Enviar resultado',
  uatAccepted: 'Aceptaste las pruebas. Ahora lo pasamos a producción.',
  uatAutoAccepted: 'Las pruebas se dieron por aceptadas al vencer el plazo.',
  uatRejected: 'Rechazaste las pruebas: el equipo ya está corrigiendo.',
  uatResult: { pending: 'Pendiente', accepted: 'Aceptado', rejected: 'Rechazado' } as Record<string, string>,
  inProduction: (d: string, w: string) => `En producción desde el ${d} · garantía hasta el ${w}.`,
  status: {
    new: 'Recibido', assigned: 'Recibido', in_progress: 'En atención', estimating: 'En estimación',
    pending_approval: 'Esperando tu aprobación', approved: 'Aprobado', on_hold: 'En pausa', on_hold_customer: 'Esperando tu respuesta',
    resolved: 'Resuelto · confirma', closed: 'Cerrado', cancelled: 'Cancelado', rejected: 'Rechazado', converted: 'Proyecto'
  } as Record<string, string>,
  types: { INC: 'Falla', SOL: 'Servicio', REQ: 'Cambio', PRY: 'Proyecto' } as Record<string, string>
};

export type PortalText = typeof es;

const en: PortalText = {
  portal: 'Support portal',
  poweredBy: 'Powered by Veta Desk',
  signIn: 'Sign in',
  signInTitle: 'Sign in to the support portal',
  signInBody: 'Enter your work email. We will send you a 6-digit code; no password needed.',
  email: 'Email',
  sendCode: 'Send me a code',
  sending: 'Sending…',
  codeSent: (email: string) => `If ${email} has access, a code is on its way. Check your spam folder too.`,
  code: 'Code',
  verify: 'Sign in',
  otherEmail: 'Use another email',
  resend: 'Resend code',
  signOut: 'Sign out',
  language: 'Español',
  myTickets: 'My tickets',
  companyTickets: 'All my company',
  newTicket: 'New ticket',
  awaitingYou: 'Awaiting your approval',
  empty: 'No tickets yet.',
  emptyBody: 'When you open a ticket, you will follow its progress here.',
  updated: 'Updated',
  requester: 'Requester',
  attendedBy: 'Handled by',
  noOneYet: 'To be assigned',
  back: '← My tickets',
  loading: 'Loading…',
  genericError: 'Something went wrong. Please try again.',
  kinds: {
    incident: ['Report a problem', 'Something stopped working or gives a wrong result.'],
    service: ['Request a service', 'A new user, an access, a question or a routine task.'],
    change: ['Request a change', 'Something new or different: a report, a setting, a rule. We estimate and quote it before starting.']
  },
  whatDoYouNeed: 'What do you need?',
  subject: 'Subject',
  subjectHint: 'One line that sums it up',
  description: 'Description',
  descriptionHint: 'What happens, since when, who is affected and how to reproduce it. You can attach files after creating it.',
  impact: 'Who is affected?',
  impactOpts: { low: 'Only me', medium: 'A team or area', high: 'The whole company' },
  urgency: 'How urgent is it?',
  urgencyOpts: { low: 'It can wait', medium: 'I need it soon', high: 'It stops operations now' },
  create: 'Open ticket',
  creating: 'Sending…',
  conversation: 'Conversation',
  you: 'You',
  support: 'Support',
  reply: 'Reply',
  replyPlaceholder: 'Write your message…',
  send: 'Send',
  attach: 'Attach file',
  uploading: 'Uploading…',
  closedNote: 'This ticket is closed. If you need anything else, open a new one.',
  resolvedTitle: 'We marked it as resolved',
  resolvedBody: 'Is everything working?',
  confirm: 'Yes, close the ticket',
  reopen: 'No, still failing',
  reopenPlaceholder: 'Tell us what is still not working',
  quoteTitle: 'Quote',
  quoteWork: 'Work',
  quotePhase: 'Phase',
  quoteFunctional: 'Functional',
  quoteSubtotal: 'Subtotal',
  quoteMgmt: (p: number) => `Service management (${p}%)`,
  quoteTotal: 'Total',
  quoteHours: (h: number) => `${h} hours`,
  quoteBag: (h: number) => `Uses your contract hours: ${h} h will be deducted.`,
  quoteAdditional: 'Does not use your contract hours: billed separately.',
  validUntil: (d: string) => `Valid until ${d}`,
  expired: 'This quote expired. We will ask for an update.',
  approve: 'Approve',
  requestChanges: 'Request changes',
  reject: 'Reject',
  reasonChanges: 'What changes do you need?',
  reasonReject: 'Reason for rejecting',
  onlyApprover: 'An approver from your company makes the decision.',
  decision: {
    approved: (by: string) => `Approved by ${by}`,
    rejected: (by: string) => `Rejected by ${by}`,
    changes: (by: string) => `${by} requested changes`
  },
  statements: 'Statements',
  statement: 'Statement',
  statementsIntro: 'Every month we send you your contract statement. Review and accept it, or request a clarification if something does not match.',
  noStatements: 'No statements yet.',
  statementStatus: { sent: 'To review', in_clarification: 'Under clarification', closed: 'Closed' } as Record<string, string>,
  stmtCharged: 'Hours charged to the contract',
  stmtAdjustments: 'Adjustments',
  stmtBagUsed: (used: number, max: number) => `Hour bank: ${used} of ${max} h used`,
  stmtBagLeft: (h: number) => `${h} h left`,
  stmtBaseline: (used: number, avail: number) => `${used} h of ${avail} h available this month`,
  stmtInBand: (h: number) => `${h} h within the flex band (deducted later)`,
  stmtOutOfBand: (h: number) => `${h} h over the flex band (billed separately)`,
  stmtRollover: (h: number) => `${h} h roll over to next month`,
  stmtTicket: 'Ticket',
  stmtHours: 'Hours',
  stmtNoTicket: 'Contract activities without a ticket',
  stmtDue: (d: string) => `If we receive no clarification requests, it will be considered accepted on ${d}.`,
  stmtAccept: 'Accept',
  stmtClarify: 'Request clarification',
  stmtClarifyLine: 'Clarify',
  stmtClarifyWhat: 'What needs clarification?',
  stmtAccepted: (by: string) => `Accepted by ${by}`,
  stmtAutoAccepted: 'Accepted when the review period ended',
  stmtClosedByTeam: 'Closed by the support team',
  stmtPending: 'Awaiting answer',
  stmtAnswer: 'Answer',
  stmtPrint: 'Print / PDF',
  adjustmentKind: { courtesy: 'Courtesy', warranty: 'Warranty', reclass: 'Reclassification', extra_charge: 'Extra charge', prior_period: 'Prior period' } as Record<string, string>,
  cancel: 'Cancel',
  confirmAction: 'Confirm',
  delivery: {
    development: 'In development', uat: 'Your turn to test', correction: 'Being corrected', deployment: 'Moving to production', closed: 'Delivered'
  } as Record<string, string>,
  uatTitle: (round: number) => `User acceptance testing${round > 1 ? ` · round ${round}` : ''}`,
  uatIntro: 'Test the change and mark each acceptance criterion. If something does not work, reject it and tell us what fails.',
  uatAutoAccept: (d: string) => `If we do not hear back, testing will be considered accepted on ${d}.`,
  uatAccept: 'Accepted',
  uatReject: 'Does not work',
  uatWhat: 'What fails?',
  uatSubmit: 'Send result',
  uatAccepted: 'You accepted the testing. We will now move it to production.',
  uatAutoAccepted: 'Testing was considered accepted when the deadline passed.',
  uatRejected: 'You rejected the testing: the team is already working on the fixes.',
  uatResult: { pending: 'Pending', accepted: 'Accepted', rejected: 'Rejected' } as Record<string, string>,
  inProduction: (d: string, w: string) => `In production since ${d} · warranty until ${w}.`,
  status: {
    new: 'Received', assigned: 'Received', in_progress: 'In progress', estimating: 'Being estimated',
    pending_approval: 'Awaiting your approval', approved: 'Approved', on_hold: 'Paused', on_hold_customer: 'Waiting for your reply',
    resolved: 'Resolved · please confirm', closed: 'Closed', cancelled: 'Cancelled', rejected: 'Rejected', converted: 'Project'
  },
  types: { INC: 'Problem', SOL: 'Service', REQ: 'Change', PRY: 'Project' }
};

export const TEXT: Record<Lang, PortalText> = { es, en };

export const PortalLang = createContext<{ lang: Lang; t: PortalText; setLang: (l: Lang) => void }>({ lang: 'es', t: es, setLang: () => {} });
export const usePortalText = () => useContext(PortalLang);

export function statusLabel(t: PortalText, status: string, holdReason: string | null) {
  if (status === 'on_hold' && holdReason === 'customer') return t.status.on_hold_customer!;
  return t.status[status] ?? status;
}

export function fmtDate(lang: Lang, iso: string | null, withTime = false) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US',
    withTime ? { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' } : { day: 'numeric', month: 'long', year: 'numeric' });
}

export function fmtMoney(lang: Lang, amount: number, currency: string) {
  try {
    return new Intl.NumberFormat(lang === 'es' ? 'es-MX' : 'en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}
